import { ImageOff, Play } from 'lucide-react';
import {
  type ComponentPropsWithoutRef,
  createContext,
  type ReactNode,
  useContext,
  useRef,
  useState
} from 'react';
import { Button as AriaButton, type ButtonProps } from 'react-aria-components';
import { MediaBadge } from 'v5/design-system/MediaBadges';
import { LayerPicture } from 'v5/design-system/MediaPicture';
import { MediaKindIcon } from 'v5/patterns/forms/fields/image-upload/MediaKindIcon';
import type { ResolvedPinMedia } from 'v5/patterns/widgets/MediaCarousel/mediaCarousel.types';
import { cn } from 'v5/platform/dom/cn';

/** A picture of a strip: its pin, its media; a file without one shows its kind. */
export interface StripPicture {
  id: number;
  media: ResolvedPinMedia | null;
  /** The file's name and type, for its kind's icon. */
  name: string;
  mimeType?: string;
}

const PointAt = createContext<(id: number) => void>(() => {});

/**
 * Pictures side by side as one button that opens them at the one pressed: the square under the
 * pointer, from the keyboard the first; a press on it never reaches the row or tile it sits in.
 * The host lays out its squares (`PictureStripSquare`) and the "+N" over the last
 * (`MediaMoreOverlay`).
 */
export function PictureStrip({
  label,
  first,
  onOpen,
  className,
  children,
  ...button
}: Omit<ButtonProps, 'onPress' | 'aria-label' | 'className' | 'children'> & {
  label: string;
  /** The picture the keyboard opens. */
  first: number | undefined;
  onOpen: (id: number | undefined) => void;
  className?: string;
  children: ReactNode;
}) {
  const pointed = useRef<number | null>(null);
  return (
    <PointAt.Provider
      value={id => {
        pointed.current = id;
      }}
    >
      <AriaButton
        {...button}
        aria-label={label}
        onPress={event => {
          const id =
            event.pointerType === 'keyboard'
              ? first
              : (pointed.current ?? first);
          pointed.current = null;
          onOpen(id);
        }}
        className={className}
      >
        {children}
      </AriaButton>
    </PointAt.Provider>
  );
}

/** One picture of a `PictureStrip`, filling the box the host sizes; `children` lie over it. */
export function PictureStripSquare({
  picture,
  className,
  children,
  onPointerDown,
  ...span
}: ComponentPropsWithoutRef<'span'> & { picture: StripPicture }) {
  const pointAt = useContext(PointAt);
  return (
    <span
      {...span}
      // the strip is one button: the square says which picture it is as the press starts
      onPointerDown={event => {
        pointAt(picture.id);
        onPointerDown?.(event);
      }}
      className={cn(
        'relative overflow-hidden rounded-sm bg-bg-subtle',
        className
      )}
    >
      <PictureStripMedia picture={picture} />
      {children}
    </span>
  );
}

/** A strip picture filling its box, a play mark on a video; a file with none, its kind. */
export function PictureStripMedia({
  picture: { media, name, mimeType },
  large = false
}: {
  picture: StripPicture;
  large?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  if (failed || !media) {
    return (
      <span className="grid size-full place-items-center bg-bg-subtle text-fg-muted">
        {failed ? (
          <ImageOff className={large ? 'size-6' : 'size-3.5'} aria-hidden />
        ) : (
          <MediaKindIcon
            media={{ name, mimeType }}
            className={large ? 'size-6' : 'size-3.5'}
          />
        )}
      </span>
    );
  }
  return (
    <>
      <LayerPicture
        src={media.src}
        fallbackSrc={media.fallbackSrc}
        onFailed={() => setFailed(true)}
      />
      {media.mediaType === 'video' && (
        <MediaBadge size={large ? 'md' : 'sm'}>
          <Play className="fill-current" />
        </MediaBadge>
      )}
    </>
  );
}
